import { useQuery } from "@tanstack/vue-query";
import { type MaybeRef } from "@vueuse/core";
import { api } from "@//api";
import { type BookingApiResponse } from "./bookings";

export type BookingStatus =
  | "arrived"
  | "checkedin"
  | "scheduled"
  | "expected"
  | "noshow"
  | "unknown"
  | "seasonal"
  | "storage"
  | "cancelled"
  | "departed"
  | "overstayed"
  | "disallowed";

const listBookingsSortbyOptions = [
  "customer_name",
  "-customer_name",
  "checked_in_at",
  "-checked_in_at",
  "auto_balance",
  "-auto_balance",
  "pitch",
  "-pitch",
  "balanceDue",
  "-balanceDue",
] as const;

export type ListBookingsSortbyOption =
  (typeof listBookingsSortbyOptions)[number];

export const isListBookingsSortbyOption = (
  value: unknown,
): value is ListBookingsSortbyOption =>
  listBookingsSortbyOptions.includes(value as ListBookingsSortbyOption);

type ListBookingsFilter = Partial<{
  siteId: string | null;
  fromDt: Date;
  untilDt: Date;
  search: string;
  paid: boolean;
  status: BookingStatus | null;
  sortBy: ListBookingsSortbyOption | null;
}>;

export type ListBookingsQuery = ListBookingsFilter & PaginationQuery;

export const listBookings = async (query: ListBookingsQuery) => {
  const { body } = await api.get<Paginate<BookingApiResponse[]>>("/bookings", {
    query,
  });

  return {
    ...body,
    items: body.items.map((booking) => parseBookingApiResponse(booking)),
  };
};

export const useBookings = (
  query: MaybeRef<ListBookingsQuery>,
  options?: { refetchInterval?: number | false },
) => {
  const { id: currentSiteId } = useCurrentSite();

  return useQuery({
    queryKey: [
      BOOKINGS_QUERY_KEY,
      computed(() => ({
        ...unref(query),
        siteId: currentSiteId.value,
      })),
    ] as const,
    queryFn: ({ queryKey: [_prefix, filter_] }) => listBookings(unref(filter_)),

    refetchIntervalInBackground: false,
    // Defaults to a 60s poll (the list view wants it); callers like the
    // calendar, which is expensive to re-render, can pass false to opt out.
    refetchInterval: options?.refetchInterval ?? 60 * 1000,
  });
};
